/**
 * Branches Management Page
 * Main page component for branch management with full CRUD operations
 */

import React, { useState } from "react";
import Drawer from "../../components/drawer/drawer";
import Modal from "../../components/modal/modal";
import PageHeader from "../../components/page-header/page-header";
import { useEffectiveBranchId } from "../../hooks/use-effective-branch-id";
import { Branch, BranchManagementState } from "./types/branch.types";
import {
  BranchForm,
  BranchTable,
  BranchStatistics as BranchStatsComponent,
} from "./components";
import { useBranches } from "./hooks/use-branches";
import {
  resetBranchState,
  openEditDrawer,
  openCreateDrawer,
  closeDrawer,
  openDeleteModal,
  closeDeleteModal,
  openStatisticsModal,
  closeStatisticsModal,
  getDrawerTitle,
} from "./utils/branch-utils";
import "./branches.css";
import { toast } from "sonner";

/**
 * Main Branches page component
 * Manages branch list, creation, editing, deletion and statistics
 */
const Branches: React.FC = () => {
  const [state, setState] = useState<BranchManagementState>(resetBranchState());
  const [isLoadingStatistics, setIsLoadingStatistics] =
    useState<boolean>(false);

  // Get effective branch ID (user's branch for regular users, selected branch for super admin)
  const effectiveBranchId = useEffectiveBranchId();

  const {
    branches,
    isLoading,
    handleCreateBranch,
    handleUpdateBranch,
    handleDeleteBranch,
    handleToggleActive,
    handleSetMain,
    fetchBranchStatistics,
    // Pagination from hook
    currentPage,
    itemsPerPage,
    totalPages,
    handlePageChange,
    handleItemsPerPageChange,
  } = useBranches(effectiveBranchId);

  // TODO: Improve error handling and add proper response types
  /**
   * Handle form submission for create/edit
   */
  const handleSubmit = async (values: Partial<Branch>): Promise<void> => {
    let response: any;
    if (state.editingBranch) {
      response = await handleUpdateBranch(state.editingBranch.id, values);
    } else {
      response = await handleCreateBranch(values);
    }

    const hasSuccessField = typeof response?.success === "boolean";
    if (!hasSuccessField || response?.success) {
      toast.success(response?.message || "Операция выполнена успешно");
      setState(closeDrawer());
    } else {
      toast.error(response?.message || "Произошла ошибка");
    }
  };

  /**
   * Handle branch deletion
   */
  const handleDelete = async (): Promise<void> => {
    if (!state.deletingBranch) return;

    const success = await handleDeleteBranch(state.deletingBranch.id);
    if (success) {
      setState(closeDeleteModal());
    }
  };

  /**
   * Handle toggle active status
   */
  const handleToggleActiveStatus = async (branch: Branch): Promise<void> => {
    await handleToggleActive(branch.id);
  };

  /**
   * Handle set as main branch
   */
  const handleSetMainBranch = async (branch: Branch): Promise<void> => {
    const confirmed = window.confirm(
      `Сделать филиал "${branch.name}" главным? Текущий главный филиал будет заменен.`
    );

    if (confirmed) {
      await handleSetMain(branch.id);
    }
  };

  /**
   * Handle view statistics
   */
  const handleViewStatistics = async (branch: Branch): Promise<void> => {
    setIsLoadingStatistics(true);
    const statistics = await fetchBranchStatistics(branch.id);
    setIsLoadingStatistics(false);

    if (statistics) {
      setState(openStatisticsModal(branch, statistics));
    } else {
      alert("Не удалось загрузить статистику филиала");
    }
  };

  /**
   * Open edit drawer
   */
  const handleEditDrawer = (branch: Branch): void => {
    setState(openEditDrawer(branch));
  };

  /**
   * Open create drawer
   */
  const handleCreateDrawer = (): void => {
    setState(openCreateDrawer());
  };

  /**
   * Close drawer
   */
  const handleCloseDrawer = (): void => {
    setState(closeDrawer());
  };

  /**
   * Open delete modal
   */
  const handleDeleteModal = (branch: Branch): void => {
    setState(openDeleteModal(branch));
  };

  /**
   * Close delete modal
   */
  const handleCloseDeleteModal = (): void => {
    setState(closeDeleteModal());
  };

  /**
   * Close statistics modal
   */
  const handleCloseStatisticsModal = (): void => {
    setState(closeStatisticsModal());
  };

  return (
    <div className="branches">
      <PageHeader
        title="Филиалы"
        subtitle="Управление филиалами компании"
        actionButton={{
          text: "Добавить филиал",
          icon: "plus",
          onClick: handleCreateDrawer,
        }}
      />

      <BranchTable
        branches={branches}
        isLoading={isLoading}
        onEdit={handleEditDrawer}
        onDelete={handleDeleteModal}
        onToggleActive={handleToggleActiveStatus}
        onSetMain={handleSetMainBranch}
        onViewStatistics={handleViewStatistics}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {/* Create/Edit Drawer */}
      <Drawer
        title={getDrawerTitle(state.editingBranch)}
        isOpen={state.isDrawerOpen}
        onClose={handleCloseDrawer}
        width={600}
      >
        <BranchForm
          initialValues={state.editingBranch}
          onSubmit={handleSubmit}
          onCancel={handleCloseDrawer}
          isLoading={isLoading}
        />
      </Drawer>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={state.isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        title="Подтвердите удаление"
        onConfirm={handleDelete}
      >
        <p>Вы уверены, что хотите удалить этот филиал?</p>
        {state.deletingBranch && (
          <div className="branches__delete-info">
            <p>
              <strong>{state.deletingBranch.name}</strong>
            </p>
            {state.deletingBranch.city && (
              <p className="branches__delete-location">
                {state.deletingBranch.city}
                {state.deletingBranch.state &&
                  `, ${state.deletingBranch.state}`}
                {state.deletingBranch.country &&
                  `, ${state.deletingBranch.country}`}
              </p>
            )}
            <p className="branches__delete-warning">
              Это действие нельзя отменить. Все связанные данные будут
              сохранены.
            </p>
          </div>
        )}
      </Modal>

      {/* Statistics Modal */}
      <Modal
        isOpen={state.isStatisticsModalOpen}
        onClose={handleCloseStatisticsModal}
        title="Статистика филиала"
        onConfirm={handleCloseStatisticsModal}
      >
        {isLoadingStatistics ? (
          <div className="branches__statistics-loading">
            Загрузка статистики
          </div>
        ) : state.selectedBranchStatistics && state.editingBranch ? (
          <BranchStatsComponent
            statistics={state.selectedBranchStatistics}
            branchName={state.editingBranch.name}
          />
        ) : null}
      </Modal>
    </div>
  );
};

export default Branches;
